import React, { useState, useEffect } from 'react';
import { getPendingSuggestions, approveSuggestion, rejectSuggestion } from './services/suggestionService';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);

    useEffect(() => {
        loadSuggestions();
    }, []);

    const loadSuggestions = async () => {
        try {
            setLoading(true);
            const pending = await getPendingSuggestions();
            setSuggestions(pending);
        } catch (error) {
            console.error('Error loading suggestions:', error);
            alert('Erreur lors du chargement des suggestions');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (suggestion) => {
        if (!window.confirm('Approuver cette suggestion?')) return;

        setProcessingId(suggestion.id);
        try {
            const adminId = localStorage.getItem('anonymousUserId') || 'admin';
            await approveSuggestion(suggestion.userId, suggestion.contributionIndex, adminId);
            alert('Suggestion approuvée! Les changements sont maintenant visibles sur la carte.');
            await loadSuggestions(); // Reload list
        } catch (error) {
            console.error('Error approving suggestion:', error);
            alert('Erreur lors de l\'approbation');
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async (suggestion) => {
        if (!window.confirm('Rejeter cette suggestion?')) return;

        setProcessingId(suggestion.id);
        try {
            const adminId = localStorage.getItem('anonymousUserId') || 'admin';
            await rejectSuggestion(suggestion.userId, suggestion.contributionIndex, adminId);
            alert('Suggestion rejetée');
            await loadSuggestions(); // Reload list
        } catch (error) {
            console.error('Error rejecting suggestion:', error);
            alert('Erreur lors du rejet');
        } finally {
            setProcessingId(null);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('fr-CA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="admin-dashboard">
                <div className="admin-dashboard__loading">Chargement...</div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="admin-dashboard__header">
                <h1>Admin - Suggestions en attente</h1>
                <button className="admin-dashboard__refresh" onClick={loadSuggestions}>
                    🔄 Rafraîchir
                </button>
            </div>

            {suggestions.length === 0 ? (
                <div className="admin-dashboard__empty">
                    ✅ Aucune suggestion en attente
                </div>
            ) : (
                <div className="admin-dashboard__list">
                    {suggestions.map((suggestion) => (
                        <div key={suggestion.id} className="suggestion-card">
                            <div className="suggestion-card__header">
                                <h3>{suggestion.rinkName}</h3>
                                <span className="suggestion-card__type">
                                    {suggestion.type === 'location' ? '📍 Emplacement' : '🏒 Statut/Condition'}
                                </span>
                            </div>

                            <div className="suggestion-card__body">
                                <div className="suggestion-card__comparison">
                                    <div className="suggestion-card__column">
                                        <h4>Actuel</h4>
                                        {suggestion.type === 'location' ? (
                                            <div className="suggestion-card__location">
                                                📍 Lat: {suggestion.currentValue.location?.lat?.toFixed(6)}<br />
                                                Lng: {suggestion.currentValue.location?.lng?.toFixed(6)}
                                            </div>
                                        ) : (
                                            <>
                                                <div>Statut: {suggestion.currentValue.status}</div>
                                                <div>Condition: {suggestion.currentValue.condition}</div>
                                            </>
                                        )}
                                    </div>

                                    <div className="suggestion-card__arrow">→</div>

                                    <div className="suggestion-card__column">
                                        <h4>Suggéré</h4>
                                        {suggestion.type === 'location' ? (
                                            <div className="suggestion-card__location">
                                                📍 Lat: {suggestion.suggestedValue.location?.lat?.toFixed(6)}<br />
                                                Lng: {suggestion.suggestedValue.location?.lng?.toFixed(6)}
                                            </div>
                                        ) : (
                                            <>
                                                <div>Statut: {suggestion.suggestedValue.status}</div>
                                                <div>Condition: {suggestion.suggestedValue.condition}</div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {suggestion.reason && (
                                    <div className="suggestion-card__reason">
                                        <strong>Raison:</strong> {suggestion.reason}
                                    </div>
                                )}

                                <div className="suggestion-card__meta">
                                    <small>
                                        Par {suggestion.userName} • {formatDate(suggestion.createdAt)}
                                    </small>
                                </div>
                            </div>

                            <div className="suggestion-card__actions">
                                <button
                                    className="suggestion-card__button suggestion-card__button--approve"
                                    onClick={() => handleApprove(suggestion)}
                                    disabled={processingId === suggestion.id}
                                >
                                    {processingId === suggestion.id ? 'En cours...' : '✓ Approuver'}
                                </button>
                                <button
                                    className="suggestion-card__button suggestion-card__button--reject"
                                    onClick={() => handleReject(suggestion)}
                                    disabled={processingId === suggestion.id}
                                >
                                    {processingId === suggestion.id ? 'En cours...' : '✕ Rejeter'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
